import { Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface RowActionsProps {
  onEdit: () => void;
  isActive: boolean;
  /** Withdrawing goes through a confirmation, reactivating does not — it takes nothing away. */
  onDeactivate?: () => void;
  onActivate?: () => void;
  /** Passed only when the value can really be deleted — nothing points at it (D78). */
  onDelete?: () => void;
  /** Why it cannot be: renders an inert trash carrying the reason, instead of no trash at all. */
  deleteBlockedReason?: string;
}

/**
 * The row actions of the referential and directory screens: edit, withdraw or restore, delete.
 * Rendered only for an account that may manage — a view-only one gets no action rather than a
 * button that 403s, so the caller drops the whole column instead of disabling what is inside it.
 * The two lifecycle handlers are optional: the technician directory carries edit alone, because
 * L2a put activation on the record.
 */
const RowActions = ({ onEdit, isActive, onDeactivate, onActivate, onDelete, deleteBlockedReason }: RowActionsProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex gap-1">
      <button
        onClick={onEdit}
        className="rounded p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40"
        title={t('Edit')}
        aria-label={t('Edit')}
      >
        <Pencil className="h-4 w-4" />
      </button>
      {onDeactivate && onActivate && (
        <button
          onClick={isActive ? onDeactivate : onActivate}
          className="rounded p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          title={isActive ? t('Deactivate') : t('Activate')}
        >
          {isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
        </button>
      )}
      {onDelete && (
        <button
          onClick={onDelete}
          className="rounded p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
          title={t('Delete')}
          aria-label={t('Delete')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {!onDelete && deleteBlockedReason && (
        // aria-disabled, not disabled: the button stays focusable so its reason can be read.
        <button
          type="button"
          aria-disabled
          className="cursor-not-allowed rounded p-1.5 text-gray-400 dark:text-gray-500"
          title={deleteBlockedReason}
          aria-label={deleteBlockedReason}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default RowActions;
